import { EmailBlock, EmailGlobalStyles, DEFAULT_EMAIL_STYLES } from './types'

export function generateEmailHtml(
  blocks: EmailBlock[],
  styles: EmailGlobalStyles = DEFAULT_EMAIL_STYLES,
): string {
  const lines: string[] = []

  lines.push('<!DOCTYPE html>')
  lines.push('<html lang="en" xmlns="http://www.w3.org/1999/xhtml">')
  lines.push('<head>')
  lines.push('<meta charset="utf-8" />')
  lines.push(
    '<meta name="viewport" content="width=device-width, initial-scale=1.0" />',
  )
  lines.push('<title>Email</title>')
  lines.push(
    '<!--[if mso]><style>table,td{font-family:Arial,sans-serif;}</style><![endif]-->',
  )
  lines.push('<style>')
  lines.push(
    `body{margin:0;padding:0;background-color:${styles.backgroundColor};-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%;}`,
  )
  lines.push(
    `table{border-spacing:0;border-collapse:collapse;mso-table-lspace:0pt;mso-table-rspace:0pt;}`,
  )
  lines.push(
    `img{border:0;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;display:block;}`,
  )
  lines.push(`a{color:${styles.linkColor};text-decoration:underline;}`)
  lines.push(
    `@media only screen and (max-width:620px){.email-container{width:100%!important;}.stack-col{display:block!important;width:100%!important;}}`,
  )
  lines.push('</style>')
  lines.push('</head>')
  lines.push(
    `<body style="margin:0;padding:0;background-color:${styles.backgroundColor};"${styles.rtlEnabled ? ' dir="rtl"' : ''}>`,
  )

  // Outer wrapper table
  lines.push(
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:${styles.backgroundColor};"${styles.rtlEnabled ? ' dir="rtl"' : ''}>`,
  )
  lines.push('<tr><td align="center" style="padding:20px 10px;">')

  // Inner content table
  const borderStyle =
    styles.outerBorderSize > 0
      ? `border:${styles.outerBorderSize}px solid ${styles.outerBorderColor};`
      : ''
  lines.push(
    `<table role="presentation" class="email-container" width="${styles.contentWidth}" cellpadding="0" cellspacing="0" style="max-width:${styles.contentWidth}px;width:100%;background-color:${styles.innerColor};border-radius:8px;overflow:hidden;${borderStyle}">`,
  )

  for (const block of blocks) {
    lines.push(renderEmailBlock(block, styles))
  }

  lines.push('</table>')
  lines.push('</td></tr></table>')
  lines.push('</body></html>')

  return lines.join('\n')
}

function renderEmailBlock(
  block: EmailBlock,
  styles: EmailGlobalStyles,
): string {
  const pad = `padding:${block.paddingTop ?? 10}px ${block.paddingRight ?? 20}px ${block.paddingBottom ?? 10}px ${block.paddingLeft ?? 20}px;`
  const bgStyle = block.backgroundColor
    ? `background-color:${block.backgroundColor};`
    : ''

  switch (block.type) {
    case 'text':
      return `<tr><td style="${pad}${bgStyle}font-family:${styles.fontFamily};font-size:${block.fontSize || 16}px;line-height:${block.lineHeight || 1.6};color:${block.color || styles.textColor};text-align:${block.align || 'left'};">${block.content || ''}</td></tr>`

    case 'heading':
      return `<tr><td style="${pad}${bgStyle}font-family:${styles.fontFamily};font-size:${block.fontSize || 28}px;font-weight:${block.fontWeight || 'bold'};line-height:${block.lineHeight || 1.3};color:${block.color || '#111827'};text-align:${block.align || 'center'};">${block.content || ''}</td></tr>`

    case 'button':
      return `<tr><td style="${pad}${bgStyle}text-align:${block.align || 'center'};"><a href="${block.buttonUrl || '#'}" target="_blank" style="display:${block.buttonWidth === 'full' ? 'block' : 'inline-block'};background-color:${block.buttonBgColor || '#3b82f6'};color:${block.buttonTextColor || '#ffffff'};font-family:${styles.fontFamily};font-size:${block.fontSize || 16}px;font-weight:${block.fontWeight || '600'};text-decoration:none;padding:12px 24px;border-radius:${block.buttonBorderRadius || 4}px;text-align:center;">${block.buttonText || 'Click Here'}</a></td></tr>`

    case 'image': {
      const align = block.imageAlign || block.align || 'center'
      if (!block.src)
        return `<tr><td style="${pad}${bgStyle}text-align:${align};"><div style="background:#f3f4f6;border:2px dashed #d1d5db;padding:40px;text-align:center;color:#9ca3af;font-family:${styles.fontFamily};font-size:14px;">Image placeholder</div></td></tr>`

      const imgStyleStr = getImageStyleStr(block, false)
      const imgTag = `<img src="${block.src}" alt="${block.alt || ''}" style="${imgStyleStr}" />`
      const content = block.href
        ? `<a href="${block.href}" target="_blank" style="display:inline-block;">${imgTag}</a>`
        : imgTag
      return `<tr><td style="${pad}${bgStyle}text-align:${align};">${content}</td></tr>`
    }

    case 'video_thumbnail': {
      const thumbSrc = block.src || getYouTubeThumbnail(block.videoUrl || '')
      const playOverlay = `<div style="position:relative;display:inline-block;"><img src="${thumbSrc || ''}" alt="${block.alt || 'Watch Video'}" style="max-width:100%;height:auto;display:block;border-radius:8px;" /><div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:60px;height:60px;background:rgba(0,0,0,0.7);border-radius:50%;display:flex;align-items:center;justify-content:center;"><div style="width:0;height:0;border-top:12px solid transparent;border-bottom:12px solid transparent;border-left:20px solid #fff;margin-left:4px;"></div></div></div>`
      const wrapped = block.videoUrl
        ? `<a href="${block.videoUrl}" target="_blank" style="text-decoration:none;">${playOverlay}</a>`
        : playOverlay
      return `<tr><td style="${pad}${bgStyle}text-align:${block.align || 'center'};">${wrapped}</td></tr>`
    }

    case 'divider':
      return `<tr><td style="${pad}${bgStyle}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td style="border-top:${block.dividerThickness || 1}px ${block.dividerStyle || 'solid'} ${block.dividerColor || '#e5e7eb'};font-size:0;line-height:0;">&nbsp;</td></tr></table></td></tr>`

    case 'spacer':
      return `<tr><td style="height:${block.height || 20}px;font-size:0;line-height:0;${bgStyle}">&nbsp;</td></tr>`

    case 'columns': {
      const cols = block.columns || [[], []]
      const ratio = block.columnRatio || '50-50'
      const widths = ratioToWidths(ratio)
      let colHtml =
        '<tr><td style="' +
        pad +
        bgStyle +
        '"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>'
      for (let i = 0; i < cols.length; i++) {
        colHtml += `<td class="stack-col" width="${widths[i]}%" valign="top" style="vertical-align:top;">`
        colHtml += `<table role="presentation" width="100%" cellpadding="0" cellspacing="0">`
        for (const childBlock of cols[i]) {
          colHtml += renderEmailBlock(childBlock, styles)
        }
        if (cols[i].length === 0) {
          colHtml += `<tr><td style="padding:20px;text-align:center;color:#9ca3af;font-family:${styles.fontFamily};font-size:13px;">Drop blocks here</td></tr>`
        }
        colHtml += '</table></td>'
      }
      colHtml += '</tr></table></td></tr>'
      return colHtml
    }

    case 'unsubscribe':
      return `<tr><td style="${pad}${bgStyle}font-family:${styles.fontFamily};font-size:${block.fontSize || 12}px;line-height:${block.lineHeight || 1.5};color:${block.color || '#9ca3af'};text-align:${block.align || 'center'};">${block.content || '<a href="{{unsubscribe_link}}" style="color:' + (styles.linkColor || '#6b7280') + ';">Unsubscribe</a>'}</td></tr>`

    case 'social': {
      const links = block.socialLinks || []
      const size = block.socialIconSize || 32
      let socialHtml = `<tr><td style="${pad}${bgStyle}text-align:${block.align || 'center'};"><table role="presentation" cellpadding="0" cellspacing="0" style="margin:0 auto;"><tr>`
      for (const link of links) {
        const iconUrl = getSocialIconUrl(link.platform, size)
        socialHtml += `<td style="padding:0 8px;"><a href="${link.url}" target="_blank"><img src="${iconUrl}" width="${size}" height="${size}" alt="${link.platform}" style="display:block;border:0;" /></a></td>`
      }
      socialHtml += `</tr></table></td></tr>`
      return socialHtml
    }

    case 'image_text': {
      const imgWidth = block.imageTextImageWidth || 50
      const textWidth = 100 - imgWidth
      const imgStyleStr = getImageStyleStr(block, true)

      const imgTag = block.imageTextImageSrc
        ? `<img src="${block.imageTextImageSrc}" alt="${block.imageTextImageAlt || ''}" style="${imgStyleStr}" />`
        : `<div style="background:#f3f4f6;border:2px dashed #d1d5db;padding:20px;text-align:center;color:#9ca3af;font-family:${styles.fontFamily};font-size:14px;">Image</div>`
      const textTag = `<div style="font-family:${styles.fontFamily};font-size:${block.fontSize || 16}px;line-height:${block.lineHeight || 1.6};color:${block.color || styles.textColor};text-align:${block.align || 'left'};">${block.imageTextContent || ''}</div>`

      const imgCol = `<td class="stack-col" width="${imgWidth}%" valign="top" style="vertical-align:top;padding:10px;">${imgTag}</td>`
      const textCol = `<td class="stack-col" width="${textWidth}%" valign="top" style="vertical-align:top;padding:10px;">${textTag}</td>`

      const cols =
        block.imageTextLayout === 'image-right'
          ? textCol + imgCol
          : imgCol + textCol

      return `<tr><td style="${pad}${bgStyle}"><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>${cols}</tr></table></td></tr>`
    }

    case 'footer': {
      let linksHtml = ''
      if (block.footerLinks && block.footerLinks.length > 0) {
        linksHtml =
          `<div style="margin-bottom:10px;">` +
          block.footerLinks
            .map(
              (l) =>
                `<a href="${l.url}" style="color:${styles.linkColor};text-decoration:underline;margin:0 10px;">${l.label}</a>`,
            )
            .join('') +
          `</div>`
      }
      return `<tr><td style="${pad}${bgStyle}font-family:${styles.fontFamily};font-size:${block.fontSize || 12}px;line-height:${block.lineHeight || 1.5};color:${block.color || '#9ca3af'};text-align:${block.align || 'center'};">
        ${linksHtml}
        <div style="margin-bottom:5px;font-weight:bold;">${block.footerCompanyName || ''}</div>
        <div>${block.footerAddress || ''}</div>
      </td></tr>`
    }

    case 'table': {
      const borderAttr = block.tableData
        ? 'border="0"'
        : 'border="1" bordercolor="#e5e7eb"'
      const paddingAttr = block.tableData
        ? 'cellpadding="0"'
        : 'cellpadding="10"'
      return `<tr><td style="${pad}${bgStyle}"><table role="presentation" width="100%" ${paddingAttr} cellspacing="0" style="border-collapse:collapse;width:100%;font-family:${styles.fontFamily};font-size:${block.fontSize || 14}px;color:${block.color || styles.textColor};text-align:${block.align || 'left'};" ${borderAttr}>
        ${block.content || ''}
      </table></td></tr>`
    }

    case 'boxed_text': {
      const borderStyle = block.boxedTextBorderColor
        ? `border:1px solid ${block.boxedTextBorderColor};`
        : ''
      const radiusStyle = block.boxedTextBorderRadius
        ? `border-radius:${block.boxedTextBorderRadius}px;`
        : ''
      const boxBg = block.boxedTextBgColor
        ? `background-color:${block.boxedTextBgColor};`
        : ''
      return `<tr><td style="${pad}${bgStyle}"><div style="${borderStyle}${radiusStyle}${boxBg}padding:20px;font-family:${styles.fontFamily};font-size:${block.fontSize || 16}px;line-height:${block.lineHeight || 1.6};color:${block.color || styles.textColor};text-align:${block.align || 'left'};">${block.content || ''}</div></td></tr>`
    }

    case 'html_block': {
      return `<tr><td style="${pad}${bgStyle}">${block.htmlContent || ''}</td></tr>`
    }

    default:
      return ''
  }
}

function ratioToWidths(ratio: string): number[] {
  switch (ratio) {
    case '50-50':
      return [50, 50]
    case '33-67':
      return [33, 67]
    case '67-33':
      return [67, 33]
    case '33-33-33':
      return [33, 33, 34]
    default:
      return [50, 50]
  }
}

function getYouTubeThumbnail(url: string): string {
  if (!url) return ''
  const match = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([a-zA-Z0-9_-]{11})/,
  )
  if (match) return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`
  return ''
}

function getSocialIconUrl(platform: string, size: number): string {
  const s = size || 32
  const base = 'https://cdn.jsdelivr.net/npm/simple-icons@v9/icons'
  switch (platform) {
    case 'facebook':
      return `${base}/facebook.svg`
    case 'twitter':
      return `${base}/twitter.svg`
    case 'instagram':
      return `${base}/instagram.svg`
    case 'linkedin':
      return `${base}/linkedin.svg`
    case 'youtube':
      return `${base}/youtube.svg`
    default:
      return `${base}/link.svg`
  }
}

function getImageStyleStr(block: EmailBlock, isImageText: boolean): string {
  let imgStyleStr = ''

  const widthProp = isImageText ? 'imageTextImageWidth' : 'imageWidth'
  const sizing = block.imageSizing || 'maxWidth'

  if (sizing === 'natural') {
    imgStyleStr += 'max-width:none;'
  } else if (sizing === 'custom') {
    if (block.imageHeight) imgStyleStr += `height:${block.imageHeight}px;`
    else imgStyleStr += 'height:auto;'

    if (block.imageWidth) imgStyleStr += `width:${block.imageWidth}px;`

    if (block.imageMaxWidth) imgStyleStr += `max-width:${block.imageMaxWidth}%;`
    else imgStyleStr += `max-width:100%;`
  } else {
    // maxWidth (default)
    const w =
      (block as any)[widthProp] ||
      block.imageMaxWidth ||
      (isImageText ? 100 : 100)
    imgStyleStr += `width:${w}%;max-width:100%;height:auto;`
  }

  if (block.imageMaxHeight)
    imgStyleStr += `max-height:${block.imageMaxHeight}px;`
  if (block.imageObjectFit) imgStyleStr += `object-fit:${block.imageObjectFit};`
  if (block.imageBorderRadius)
    imgStyleStr += `border-radius:${block.imageBorderRadius}px;`
  if (block.imageOpacity != null && block.imageOpacity < 100)
    imgStyleStr += `opacity:${block.imageOpacity / 100};`

  if (block.imageShadow === 'sm')
    imgStyleStr += 'box-shadow:0 1px 2px 0 rgba(0,0,0,0.05);'
  else if (block.imageShadow === 'md')
    imgStyleStr += 'box-shadow:0 4px 6px -1px rgba(0,0,0,0.1);'
  else if (block.imageShadow === 'lg')
    imgStyleStr += 'box-shadow:0 10px 15px -3px rgba(0,0,0,0.1);'

  const align = block.imageAlign || block.align || 'center'
  if (align === 'center') imgStyleStr += 'margin:0 auto;'
  else if (align === 'right') imgStyleStr += 'margin-left:auto;'
  else if (align === 'left') imgStyleStr += 'margin-right:auto;'

  imgStyleStr += 'display:block;'
  return imgStyleStr
}
